import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { updateAppointment, createAppointmentInDb } from '../../services/appointmentService';
import { convertTimestampsToISO } from '../../utils/firebaseUtils.js';

export const createAppointment = createAsyncThunk(
  'appointments/createAppointment',
  async (appointmentData) => {
    const result = await createAppointmentInDb(appointmentData);
    return { id: result.id, ...convertTimestampsToISO(appointmentData) };
  }
);

export const updateAppointmentAsync = createAsyncThunk(
  'appointments/updateAppointment',
  async ({ appointmentId, updateData }) => {
    await updateAppointment(appointmentId, updateData);
    return { id: appointmentId, ...convertTimestampsToISO(updateData) };
  }
);

const initialState = {
  appointments: [],
  selectedAppointment: null,
  loading: false,
  error: null,
};

const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    setAppointments: (state, action) => {
      state.appointments = action.payload;
      state.error = null;
    },
    setSelectedAppointment: (state, action) => {
      state.selectedAppointment = action.payload;
      state.error = null;
    },
    addAppointment: (state, action) => {
      state.appointments.push(action.payload);
      state.error = null;
    },
    deleteAppointment: (state, action) => {
      state.appointments = state.appointments.filter(apt => apt.id !== action.payload);
      if (state.selectedAppointment?.id === action.payload) {
        state.selectedAppointment = null;
      }
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments.push(action.payload);
        state.selectedAppointment = action.payload;
      })
      .addCase(createAppointment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateAppointmentAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppointmentAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.appointments.findIndex(apt => apt.id === action.payload.id);
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
        if (state.selectedAppointment?.id === action.payload.id) {
          state.selectedAppointment = action.payload;
        }
      })
      .addCase(updateAppointmentAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setAppointments,
  setSelectedAppointment,
  addAppointment,
  deleteAppointment,
  setLoading,
  setError,
  clearError,
} = appointmentsSlice.actions;

export default appointmentsSlice.reducer;
